/**
 * Money (Foundry: item/physical/coins.ts): four denominations, arithmetic in copper, prices
 * parsed from "5 gp" strings and printed back. Starfinder's credits and UPB are not carried.
 */
export const COIN_DENOMINATIONS = ["pp", "gp", "sp", "cp"] as const;
export type Denomination = (typeof COIN_DENOMINATIONS)[number];
export const DENOMINATION_RATES: Readonly<Record<Denomination, number>> = { cp: 1, sp: 10, gp: 100, pp: 1000 };

export type RawCoins = Partial<Record<Denomination, number>>;

export function isDenomination(value: unknown): value is Denomination {
  return typeof value === "string" && (COIN_DENOMINATIONS as readonly string[]).includes(value);
}

export class Coins {
  readonly pp: number;
  readonly gp: number;
  readonly sp: number;
  readonly cp: number;

  constructor(data?: RawCoins | number | null) {
    const object: RawCoins = typeof data === "number" ? { cp: data } : (data ?? {});
    const clean = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? Math.max(Math.floor(Math.abs(v)), 0) : 0);
    this.pp = clean(object.pp);
    this.gp = clean(object.gp);
    this.sp = clean(object.sp);
    this.cp = clean(object.cp);
  }

  get copperValue(): number {
    return COIN_DENOMINATIONS.reduce((total, d) => total + this[d] * DENOMINATION_RATES[d], 0);
  }

  get goldValue(): number {
    return this.copperValue / 100;
  }

  get isEmpty(): boolean {
    return this.copperValue === 0;
  }

  plus(coins: RawCoins | Coins): Coins {
    const other = coins instanceof Coins ? coins : new Coins(coins);
    return new Coins({ pp: this.pp + other.pp, gp: this.gp + other.gp, sp: this.sp + other.sp, cp: this.cp + other.cp });
  }

  /** Multiply, spilling fractions of a coin into the smaller denominations (Foundry's `scale`). */
  scale(factor: number): Coins {
    const result: Record<Denomination, number> = { pp: this.pp * factor, gp: this.gp * factor, sp: this.sp * factor, cp: this.cp * factor };
    if (factor % 1 !== 0) {
      result.gp += (result.pp % 1) * 10;
      result.sp += (result.gp % 1) * 10;
      result.cp += (result.sp % 1) * 10;
      for (const d of COIN_DENOMINATIONS) result[d] = Math.floor(Number(result[d].toFixed(1)));
    }
    return new Coins(result);
  }

  /** A larger item costs more: large double, huge four times, gargantuan eight times (Player Core). */
  adjustForSize(size: string): Coins {
    switch (size) {
      case "lg": return this.scale(2);
      case "huge": return this.scale(4);
      case "grg": return this.scale(8);
      default: return new Coins(this);
    }
  }

  /** Denominations with a value, for saving. */
  toObject(): RawCoins {
    const out: RawCoins = {};
    for (const d of COIN_DENOMINATIONS) if (this[d]) out[d] = this[d];
    return out;
  }

  /** Foundry's `fromPrice`: a price for `factor` items, `per` items to the price. */
  static fromPrice(price: { value?: RawCoins | null; per?: number | null } | null | undefined, factor: number): Coins {
    const per = Math.max(1, price?.per ?? 1);
    return new Coins(price?.value ?? {}).scale(factor / per);
  }

  /** Parse "5 gp", "12 sp, 3 cp" or a bare number of gold pieces. */
  static fromString(text: string, quantity = 1): Coins {
    let source = text.trim().replace(/,/g, "");
    if (/^\d+$/.test(source)) source = `${source} gp`;
    const result: RawCoins = {};
    for (const match of source.matchAll(/(\d+)\s*([pgsc]p)/g)) {
      const denomination = match[2]!;
      if (isDenomination(denomination)) result[denomination] = (Number(match[1]) || 0) * quantity;
    }
    return new Coins(result);
  }

  /** Fold copper into silver and gold (platinum is left as it is); Foundry's `normalized`. */
  normalized(): Coins {
    let cp = this.copperValue;
    const gp = Math.floor(cp / 100);
    cp -= gp * 100;
    const sp = Math.floor(cp / 10);
    cp -= sp * 10;
    return new Coins({ gp, sp, cp });
  }

  /** "5 gp", "12 gp, 3 sp", "0 gp": every denomination from largest to smallest. */
  toString(options: { normalize?: boolean } = {}): string {
    const coins = options.normalize === false ? this : this.normalized();
    const parts = COIN_DENOMINATIONS.filter((d) => coins[d] > 0).map((d) => `${coins[d]} ${d}`);
    return parts.length > 0 ? parts.join(", ") : "0 gp";
  }

  /** "12.35 gp": the total in gold with two decimals. */
  toGoldString(): string {
    return `${this.goldValue.toFixed(2)} gp`;
  }
}
